/**
 * Stage editor for one pipeline definition (#885): the per-kind table of Phase 4's typed stage
 * schema (`services/pipeline_definitions.py`), plus the draft model the Pipelines tab publishes.
 *
 *  - deal: label, colour, probability. Existing keys are read-only. Open stages can be added and
 *    removed; Closed Won and Closed Lost are locked (rename and recolour only).
 *  - contact (Lead): label, colour, ball in court, outcome (Open / Won / Lost), amber/red days, and
 *    where new form leads land.
 *  - event_contact, schema 1: two fixed groups (before / after the event), each with a default
 *    follow-up; per stage a follow-up override, a Terminal flag and its group.
 *  - event_contact, schema 2 (the event journey, #1013): six fixed milestone columns (To invite,
 *    Invited, Going, Attended, No-show, Closed) that can only be RENAMED, then the follow-up stages
 *    between No-show and Closed (add, rename, remove, reorder, follow-up days, Terminal), then the
 *    two follow-up defaults. The default stage is always To invite.
 *
 * A stage's key is generated when it is created and shown as "(new)" until published; the server
 * names the stage and the field in every rule it rejects, so only "needs a label" is checked here.
 */
import React from 'react';
import { ArrowDown, ArrowUp, Lock, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { isClosedDealStage } from '../../lib/dealStages';
import { LEAD_OUTCOME_OPTIONS, leadOutcomeForKey, leadStageOutcome } from '../../lib/leadStages';
import { dealStageKeyFromLabel, stageKeyFromLabel } from '../../lib/stageKeys';

export const BALL_OPTIONS = [
    { value: 'action_needed', label: 'Action Needed' },
    { value: 'waiting_on_lead', label: 'Waiting on Lead' },
    { value: 'na', label: 'N/A' },
];
const ANCHOR_OPTIONS = [
    { value: 'stage_entered', label: 'after the stage is entered' },
    { value: 'event_date', label: 'after the event date' },
];
const NEW_STAGE_COLOR = { deal: '#78716c', contact: '#64748b', event_contact: '#64748b' };
// The six fixed milestone columns of an event journey (schema 2), with what moves a guest into each.
export const MILESTONES = [
    { key: 'to_invite', label: 'To invite', hint: 'Where every guest starts. Moved to by hand.' },
    { key: 'invited', label: 'Invited', hint: 'Moves on its own when an invite is sent or marked sent.', auto: true },
    { key: 'going', label: 'Going', hint: 'Moves on its own when they RSVP yes.', auto: true },
    { key: 'attended', label: 'Attended', hint: 'Moves on its own on check-in, a face match or a walk-in.', auto: true },
    { key: 'no_show', label: 'No-show', hint: 'Filled when the event ends.', auto: true },
    { key: 'closed', label: 'Closed', hint: 'Not going further: declined, no reply, not interested or rescheduled.' },
];
const MILESTONE_LABEL_MAX = 40;
// The backend's label limit, and the tighter one for a new deal stage, whose key is its label (P14).
const LABEL_MAX = 60;
const DEAL_KEY_MAX = 40;

const inputCls = 'px-2 py-1.5 text-sm border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10 disabled:bg-stone-50 disabled:text-stone-500';
// Literal class names: Tailwind only generates arbitrary grid templates it can see in the source.
// `xl:`, not `sm:` or `lg:`: the admin sidebar sits in the page flow, so the editor card is about
// the viewport minus 456px wide (its rows are 790px at 1280px). The Lead row has eight tracks and fits
// there only because the Ball in court select keeps 9rem, enough for "Waiting on Lead"; the label takes
// the rest (about 168px). Below `xl:` the controls stack two to a line and each carries its own caption
// (the column header row is `xl:` too).
const GRID = {
    deal: 'xl:grid-cols-[1.6fr_2.4rem_5rem_1fr_5.5rem]',
    contact: 'xl:grid-cols-[1.4fr_2.4rem_minmax(9rem,1fr)_5.5rem_4rem_4rem_5rem_5.5rem]',
    event_contact: 'xl:grid-cols-[1.4fr_2.4rem_5.5rem_5rem_10rem_1fr_5.5rem]',
    journey: 'xl:grid-cols-[1.4fr_2.4rem_5.5rem_5rem_1fr_5.5rem]',
};
/** One control of a stage row, with a caption that only shows when the column header row does not. */
export function Field({ caption, children }) {
    return (
        <label className="flex flex-col gap-0.5 min-w-0">
            <span className="text-[11px] text-stone-400 xl:hidden">{caption}</span>
            {children}
        </label>
    );
}

const HEADERS = {
    deal: ['Label', 'Color', 'Probability %', 'Stored as', ''],
    contact: ['Label', 'Color', 'Ball in court', 'Outcome', 'Amber d', 'Red d', 'Stored as', ''],
    event_contact: ['Label', 'Color', 'Follow up d', 'Terminal', 'Group', 'Stored as', ''],
    journey: ['Label', 'Color', 'Follow up d', 'Terminal', 'Stored as', ''],
};

let newStageCounter = 0;

// ── draft model ──────────────────────────────────────────────────────────────

const text = (value) => (value === null || value === undefined ? '' : value);
const toNumber = (value) => (value === '' || value === null || value === undefined ? null : Number(value));
const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

/** A client-side row for an existing stage. `uid` is the key; new rows get `new-<n>` until published. */
function draftStage(kind, stage) {
    const row = { uid: stage.key, key: stage.key, isNew: false, label: text(stage.label), color: stage.color || NEW_STAGE_COLOR[kind] };
    if (kind === 'deal') row.probability = text(stage.probability);
    if (kind === 'contact') {
        Object.assign(row, {
            amber_days: text(stage.amber_days), red_days: text(stage.red_days), ball_in_court: stage.ball_in_court || 'action_needed',
            // Stages saved before outcomes carry none: read the key default, so a first save keeps `won` / `lost` as they were.
            outcome: leadStageOutcome(stage),
        });
    }
    if (kind === 'event_contact') {
        Object.assign(row, { group: stage.group, follow_up_days: text(stage.follow_up_days), terminal: !!stage.terminal });
    }
    return row;
}

function newDraftStage(kind, group) {
    newStageCounter += 1;
    const base = { uid: `new-${newStageCounter}`, key: '', isNew: true, label: '', color: NEW_STAGE_COLOR[kind] };
    if (kind === 'deal') return { ...base, probability: 50 };
    if (kind === 'contact') return { ...base, amber_days: 3, red_days: 7, ball_in_court: 'action_needed', outcome: 'open' };
    return { ...base, group, follow_up_days: '', terminal: false };
}

/** The editable draft of a definition: rows in order, the default stage as a row uid, the event groups. */
export function draftFromDefinition(definition) {
    const kind = definition.record_kind;
    const stages = [...(definition.stages || [])].sort(byOrder).map((s) => draftStage(kind, s));
    const groups = kind === 'event_contact'
        ? (definition.groups || []).map((g) => ({ ...g, default_follow_up_days: text(g.default_follow_up_days) }))
        : null;
    const defaultRow = stages.find((s) => s.key === definition.default_stage);
    const draft = { stages, defaultUid: defaultRow ? defaultRow.uid : null, groups };
    if (isJourney(kind, definition)) {
        // The journey (#1013): the milestones are the server's six keys in its order, whatever it sent.
        const stored = new Map((definition.milestones || []).map((m) => [m.key, m.label]));
        draft.schema = 2;
        draft.milestones = MILESTONES.map((m) => ({ key: m.key, label: text(stored.get(m.key) ?? m.label) }));
    }
    return reconcileDefault(kind, draft);
}

/** An event pipeline on schema 2, the journey. Anything else (and every older definition) is schema 1. */
const isJourney = (kind, definition) => kind === 'event_contact' && Number(definition?.schema) === 2;

const isLocked = (kind, stage) => kind === 'deal' && !stage.isNew && isClosedDealStage(stage.key);
const canBeDefault = (kind, stage) => (
    kind === 'contact' || (kind === 'deal' ? !isLocked(kind, stage) : stage.group === 'before_event'));

/** Keep the default stage valid after an edit. A contact default of null ("don't place new leads") is a choice, kept. */
function reconcileDefault(kind, draft) {
    if (draft.schema === 2) return { ...draft, defaultUid: null };     // a journey always starts at To invite
    const current = draft.stages.find((s) => s.uid === draft.defaultUid);
    if (current && canBeDefault(kind, current)) return draft;
    if (kind === 'contact' && draft.defaultUid === null) return draft;
    const fallback = draft.stages.find((s) => canBeDefault(kind, s));
    return { ...draft, defaultUid: fallback ? fallback.uid : null };
}

function serializeStage(kind, s, key, order) {
    const stage = { key, label: s.label.trim(), color: s.color || NEW_STAGE_COLOR[kind], order };
    if (kind === 'deal') stage.probability = toNumber(s.probability);
    if (kind === 'contact') {
        Object.assign(stage, {
            amber_days: toNumber(s.amber_days), red_days: toNumber(s.red_days), ball_in_court: s.ball_in_court || 'action_needed',
            outcome: s.outcome || leadOutcomeForKey(key),
        });
    }
    if (kind === 'event_contact') {
        Object.assign(stage, { group: s.group, follow_up_days: toNumber(s.follow_up_days), terminal: !!s.terminal });
    }
    return stage;
}

/** The publish payload (without `expected_version`). New stages get their key here, from their label. */
export function serializeDraft(kind, draft) {
    const used = new Set(draft.stages.filter((s) => !s.isNew).map((s) => s.key));
    const keys = new Map();
    const stages = draft.stages.map((s, order) => {
        let key = s.key;
        if (s.isNew) {
            key = kind === 'deal' ? dealStageKeyFromLabel(s.label) : stageKeyFromLabel(s.label, used);
            used.add(key);
        }
        keys.set(s.uid, key);
        return serializeStage(kind, s, key, order);
    });
    const body = { stages, default_stage: draft.defaultUid === null ? null : (keys.get(draft.defaultUid) ?? null) };
    if (kind === 'event_contact') {
        body.groups = draft.groups.map((g) => ({
            key: g.key, label: g.label, default_follow_up_days: toNumber(g.default_follow_up_days), follow_up_anchor: g.follow_up_anchor,
        }));
    }
    if (draft.schema === 2) {
        body.schema = 2;
        body.default_stage = 'to_invite';
        body.milestones = draft.milestones.map((m) => ({ key: m.key, label: m.label.trim() }));
    }
    return body;
}

/** A reason the draft cannot be published, or null. The server owns every other rule and names the stage.
 *  A blank deal probability is caught here because the server would silently restore the stage's old
 *  value for it, and the admin would think they had cleared it. */
export function draftProblem(draft) {
    const badMilestone = (draft.milestones || []).find((m) => !m.label.trim() || m.label.trim().length > MILESTONE_LABEL_MAX);
    if (badMilestone) return `The ${badMilestone.key.replace('_', ' ')} column needs a name of 1 to ${MILESTONE_LABEL_MAX} characters`;
    if (draft.stages.some((s) => !(s.label || '').trim())) return 'Every stage needs a label';
    const bad = draft.stages.find((s) => 'probability' in s
        && !(String(s.probability).trim() !== '' && Number.isInteger(Number(s.probability)) && Number(s.probability) >= 0 && Number(s.probability) <= 100));
    return bad ? `Probability for '${bad.label.trim()}' must be a whole number from 0 to 100` : null;
}

// ── editing helpers ──────────────────────────────────────────────────────────

/** The row a stage would swap with, or -1. A locked row (Closed Won/Lost) is never swapped past; in
 *  event pipelines a stage only moves among the stages of its own group. */
function swapTarget(kind, stages, index, dir) {
    for (let j = index + dir; j >= 0 && j < stages.length; j += dir) {
        if (kind === 'event_contact' && stages[j].group !== stages[index].group) continue;
        return isLocked(kind, stages[j]) ? -1 : j;
    }
    return -1;
}

// ── the editor ───────────────────────────────────────────────────────────────

export default function PipelineStageEditor({ kind, draft, onChange, disabled = false }) {
    const { stages, groups } = draft;
    const journey = draft.schema === 2;
    const gridKey = journey ? 'journey' : kind;
    const patchMilestone = (key, label) => onChange({ ...draft, milestones: draft.milestones.map((m) => (m.key === key ? { ...m, label } : m)) });
    const commit = (next) => onChange(reconcileDefault(kind, next));
    const setStages = (next) => commit({ ...draft, stages: next });

    const patchStage = (uid, patch) => setStages(stages.map((s) => (s.uid === uid ? { ...s, ...patch } : s)));
    const patchGroup = (key, patch) => commit({ ...draft, groups: groups.map((g) => (g.key === key ? { ...g, ...patch } : g)) });
    const removeStage = (uid) => setStages(stages.filter((s) => s.uid !== uid));
    const addStage = (group) => {
        const added = newDraftStage(kind, group);
        // Open deal stages stay ahead of the locked closed pair.
        const lockedAt = kind === 'deal' ? stages.findIndex((s) => isLocked(kind, s)) : -1;
        const at = lockedAt === -1 ? stages.length : lockedAt;
        setStages([...stages.slice(0, at), added, ...stages.slice(at)]);
    };
    const moveStage = (uid, dir) => {
        const from = stages.findIndex((s) => s.uid === uid);
        const to = swapTarget(kind, stages, from, dir);
        if (to === -1) return;
        const next = [...stages];
        [next[from], next[to]] = [next[to], next[from]];
        setStages(next);
    };

    const renderRow = (s) => {
        const locked = isLocked(kind, s);
        // A row's controls are named by the stage they belong to (its stored key, or "new stage N"),
        // so a screen reader lists "Probability % for Qualified" instead of eight identical "Probability %".
        const rowName = s.isNew ? `new stage ${stages.indexOf(s) + 1}` : s.key;
        const up = swapTarget(kind, stages, stages.indexOf(s), -1) === -1;
        const down = swapTarget(kind, stages, stages.indexOf(s), 1) === -1;
        return (
            <div key={s.uid} data-testid={`stage-row-${s.uid}`}
                 className={`grid grid-cols-2 ${GRID[gridKey]} gap-2 items-end xl:items-center`}>
                <Field caption="Label">
                    <input className={inputCls} value={s.label} placeholder="Stage label" aria-label={`Label for ${rowName}`} disabled={disabled}
                           maxLength={s.isNew && kind === 'deal' ? DEAL_KEY_MAX : LABEL_MAX}
                           data-testid={`stage-label-${s.uid}`} onChange={(e) => patchStage(s.uid, { label: e.target.value })} />
                </Field>
                <Field caption="Color">
                    <input type="color" aria-label={`Color for ${rowName}`} className="h-8 w-full rounded border border-stone-200 p-0.5" disabled={disabled}
                           data-testid={`stage-color-${s.uid}`} value={s.color || NEW_STAGE_COLOR[kind]}
                           onChange={(e) => patchStage(s.uid, { color: e.target.value })} />
                </Field>
                {kind === 'deal' && (
                    <Field caption="Probability %">
                        <input type="number" min="0" max="100" aria-label={`Probability % for ${rowName}`} className={inputCls} value={s.probability} disabled={disabled || locked}
                               data-testid={`stage-probability-${s.uid}`} onChange={(e) => patchStage(s.uid, { probability: e.target.value })} />
                    </Field>
                )}
                {kind === 'contact' && (
                    <>
                        <Field caption="Ball in court">
                            <select className={inputCls} value={s.ball_in_court} aria-label={`Ball in court for ${rowName}`} disabled={disabled} data-testid={`stage-ball-${s.uid}`}
                                    onChange={(e) => patchStage(s.uid, { ball_in_court: e.target.value })}>
                                {BALL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </Field>
                        <Field caption="Outcome">
                            <select className={inputCls} value={s.outcome} aria-label={`Outcome for ${rowName}`} disabled={disabled} data-testid={`stage-outcome-${s.uid}`}
                                    title="A Won or Lost stage never shows the Follow up badge, and dropping a card on it asks for the sale details or the reason lost"
                                    onChange={(e) => patchStage(s.uid, { outcome: e.target.value })}>
                                {LEAD_OUTCOME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </Field>
                        <Field caption="Amber d">
                            <input type="number" min="0" aria-label={`Amber days for ${rowName}`} className={inputCls} value={s.amber_days} placeholder="—" disabled={disabled}
                                   data-testid={`stage-amber-${s.uid}`} onChange={(e) => patchStage(s.uid, { amber_days: e.target.value })} />
                        </Field>
                        <Field caption="Red d">
                            <input type="number" min="0" aria-label={`Red days for ${rowName}`} className={inputCls} value={s.red_days} placeholder="—" disabled={disabled}
                                   data-testid={`stage-red-${s.uid}`} onChange={(e) => patchStage(s.uid, { red_days: e.target.value })} />
                        </Field>
                    </>
                )}
                {kind === 'event_contact' && (
                    <>
                        <Field caption="Follow up d">
                            <input type="number" min="0" max="365" aria-label={`Follow-up days for ${rowName}`} className={inputCls} value={s.follow_up_days} placeholder="default" disabled={disabled}
                                   title="Blank uses the group's default follow-up" data-testid={`stage-followup-${s.uid}`}
                                   onChange={(e) => patchStage(s.uid, { follow_up_days: e.target.value })} />
                        </Field>
                        <label className="flex items-center gap-1.5 text-sm text-stone-600">
                            <input type="checkbox" className="rounded border-stone-300" checked={s.terminal} disabled={disabled} aria-label={`Terminal: ${rowName}`}
                                   data-testid={`stage-terminal-${s.uid}`} onChange={(e) => patchStage(s.uid, { terminal: e.target.checked })} />
                            Terminal
                        </label>
                        {!journey && (
                            <Field caption="Group">
                                <select className={inputCls} value={s.group} aria-label={`Group for ${rowName}`} disabled={disabled} data-testid={`stage-group-${s.uid}`}
                                        onChange={(e) => patchStage(s.uid, { group: e.target.value })}>
                                    {groups.map((g) => <option key={g.key} value={g.key}>{g.label || g.key}</option>)}
                                </select>
                            </Field>
                        )}
                    </>
                )}
                <div className="min-w-0">
                    <span className="block text-[11px] text-stone-400 xl:hidden">Stored as</span>
                    <span className="block font-mono text-xs text-stone-500 truncate" title={s.key} data-testid={`stage-key-${s.uid}`}>
                        {s.isNew ? '(new)' : s.key}
                    </span>
                </div>
                <div className="flex items-center gap-0.5">
                    {locked ? (
                        <span className="p-1 text-stone-400" data-testid={`stage-lock-${s.uid}`}
                              title="Closed Won and Closed Lost are locked: you can rename and recolor them, not move or remove them">
                            <Lock className="w-3.5 h-3.5" aria-hidden="true" />
                            <span className="sr-only">Locked: {rowName} cannot be moved or removed</span>
                        </span>
                    ) : (
                        <>
                            <button type="button" className="p-1 text-stone-400 hover:text-stone-700 disabled:opacity-30" title="Move up" aria-label={`Move ${rowName} up`}
                                    disabled={disabled || up}
                                    data-testid={`stage-up-${s.uid}`} onClick={() => moveStage(s.uid, -1)}><ArrowUp className="w-3.5 h-3.5" aria-hidden="true" /></button>
                            <button type="button" className="p-1 text-stone-400 hover:text-stone-700 disabled:opacity-30" title="Move down" aria-label={`Move ${rowName} down`}
                                    disabled={disabled || down}
                                    data-testid={`stage-down-${s.uid}`} onClick={() => moveStage(s.uid, 1)}><ArrowDown className="w-3.5 h-3.5" aria-hidden="true" /></button>
                            <button type="button" className="p-1 text-stone-400 hover:text-red-600 disabled:opacity-30" title="Remove" aria-label={`Remove ${rowName}`} disabled={disabled}
                                    data-testid={`stage-remove-${s.uid}`} onClick={() => removeStage(s.uid)}><Trash2 className="w-3.5 h-3.5" aria-hidden="true" /></button>
                        </>
                    )}
                </div>
            </div>
        );
    };

    const headerRow = (
        <div className={`hidden xl:grid ${GRID[gridKey]} gap-2 text-xs text-stone-500 px-1`}>
            {HEADERS[gridKey].map((h, i) => <span key={`${h}-${i}`}>{h}</span>)}
        </div>
    );

    // The journey (#1013): milestones are locked rows (rename only), follow-up stages are the editable list.
    const journeyBody = journey && (
        <>
            <section className="rounded-lg border border-stone-200" data-testid="milestone-section">
                <div className="px-4 py-3 border-b border-stone-100">
                    <h4 className="text-sm font-semibold text-stone-700">Fixed columns</h4>
                    <p className="text-xs text-stone-500 mt-0.5">
                        Every event shows these columns, in this order. You can rename them, not add, remove or reorder them.
                        The follow-up stages you add below sit between No-show and Closed.
                    </p>
                </div>
                <ul className="p-4 space-y-2">
                    {draft.milestones.map((m) => {
                        const meta = MILESTONES.find((x) => x.key === m.key) || {};
                        return (
                            <li key={m.key} data-testid={`milestone-row-${m.key}`} className="grid grid-cols-[auto_1fr] sm:grid-cols-[auto_minmax(9rem,16rem)_1fr_auto] gap-2 items-center">
                                <span className="p-1 text-stone-400" data-testid={`milestone-lock-${m.key}`}
                                      title="A fixed column: you can rename it, not move or remove it">
                                    <Lock className="w-3.5 h-3.5" aria-hidden="true" />
                                    <span className="sr-only">Locked: {m.key} cannot be moved or removed</span>
                                </span>
                                <input className={inputCls} value={m.label} maxLength={MILESTONE_LABEL_MAX} disabled={disabled}
                                       aria-label={`Name for the ${m.key.replace('_', ' ')} column`} data-testid={`milestone-label-${m.key}`}
                                       onChange={(e) => patchMilestone(m.key, e.target.value)} />
                                <span className="text-xs text-stone-500 col-span-2 sm:col-span-1">{meta.hint}</span>
                                <span className="font-mono text-xs text-stone-400" data-testid={`milestone-key-${m.key}`}>{m.key}</span>
                            </li>
                        );
                    })}
                </ul>
            </section>
            <section className="rounded-lg border border-stone-200" data-testid="follow-up-section">
                <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
                    <div>
                        <h4 className="text-sm font-semibold text-stone-700">Follow-up stages</h4>
                        <p className="text-xs text-stone-500 mt-0.5">After the event, between No-show and Closed. Mark a stage Terminal when the guest is done with the event.</p>
                    </div>
                    <Button variant="outline" size="sm" disabled={disabled} data-testid="add-stage" onClick={() => addStage('after_event')}>
                        <Plus className="w-4 h-4 mr-1" /> Add stage
                    </Button>
                </div>
                <div className="p-4 space-y-2">
                    {headerRow}
                    {stages.map(renderRow)}
                    {stages.length === 0 && <p className="text-sm text-stone-400 py-2 text-center">No follow-up stages. Guests go from Attended or No-show straight to Closed.</p>}
                </div>
            </section>
            <section className="rounded-lg border border-stone-200" data-testid="follow-up-defaults">
                <div className="px-4 py-3 border-b border-stone-100">
                    <h4 className="text-sm font-semibold text-stone-700">Follow-up timing</h4>
                    <p className="text-xs text-stone-500 mt-0.5">Used when a stage has no follow-up days of its own.</p>
                </div>
                <div className="p-4 flex flex-wrap gap-6">
                    {groups.map((g) => (
                        <div key={g.key} className="flex flex-wrap items-end gap-3" data-testid={`group-section-${g.key}`}>
                            <div>
                                <label className="block text-xs font-medium text-stone-600 mb-1" htmlFor={`group-days-${g.key}`}>
                                    {g.key === 'before_event' ? 'Nudge an invite after (days)' : 'Follow up after (days)'}
                                </label>
                                <input id={`group-days-${g.key}`} type="number" min="0" max="365" className={`w-24 ${inputCls}`} value={g.default_follow_up_days}
                                       placeholder="none" disabled={disabled} data-testid={`group-days-${g.key}`}
                                       onChange={(e) => patchGroup(g.key, { default_follow_up_days: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-600 mb-1" htmlFor={`group-anchor-${g.key}`}>Counted</label>
                                <select id={`group-anchor-${g.key}`} className={inputCls} value={g.follow_up_anchor} disabled={disabled}
                                        data-testid={`group-anchor-${g.key}`} onChange={(e) => patchGroup(g.key, { follow_up_anchor: e.target.value })}>
                                    {ANCHOR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </>
    );

    const defaultOptions = stages.filter((s) => canBeDefault(kind, s));

    return (
        <div className="space-y-4" data-testid="pipeline-stage-editor">
            {kind === 'deal' && (
                <p className="text-xs text-stone-500">
                    Closed Won and Closed Lost are locked: rename and recolor them, nothing more. Add or remove open stages;
                    removing one that still has deals asks first.
                </p>
            )}

            {journey ? journeyBody : kind === 'event_contact' ? (
                groups.map((g) => (
                    <section key={g.key} className="rounded-lg border border-stone-200" data-testid={`group-section-${g.key}`}>
                        <div className="px-4 py-3 border-b border-stone-100 flex flex-wrap items-end gap-3">
                            <div>
                                <label className="block text-xs font-medium text-stone-600 mb-1" htmlFor={`group-label-${g.key}`}>Section name</label>
                                <input id={`group-label-${g.key}`} className={inputCls} value={g.label} disabled={disabled}
                                       data-testid={`group-label-${g.key}`} onChange={(e) => patchGroup(g.key, { label: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-600 mb-1" htmlFor={`group-days-${g.key}`}>Default follow-up (days)</label>
                                <input id={`group-days-${g.key}`} type="number" min="0" max="365" className={`w-24 ${inputCls}`} value={g.default_follow_up_days}
                                       placeholder="none" disabled={disabled} data-testid={`group-days-${g.key}`}
                                       onChange={(e) => patchGroup(g.key, { default_follow_up_days: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-stone-600 mb-1" htmlFor={`group-anchor-${g.key}`}>Counted</label>
                                <select id={`group-anchor-${g.key}`} className={inputCls} value={g.follow_up_anchor} disabled={disabled}
                                        data-testid={`group-anchor-${g.key}`} onChange={(e) => patchGroup(g.key, { follow_up_anchor: e.target.value })}>
                                    {ANCHOR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                            <Button variant="outline" size="sm" className="ml-auto" disabled={disabled}
                                    data-testid={`add-stage-${g.key}`} onClick={() => addStage(g.key)}>
                                <Plus className="w-4 h-4 mr-1" /> Add stage
                            </Button>
                        </div>
                        <div className="p-4 space-y-2">
                            {headerRow}
                            {stages.filter((s) => s.group === g.key).map(renderRow)}
                            {!stages.some((s) => s.group === g.key) && <p className="text-sm text-stone-400 py-2 text-center">No stages in this section.</p>}
                        </div>
                    </section>
                ))
            ) : (
                <div className="rounded-lg border border-stone-200">
                    <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-stone-700">Stages</h4>
                        <Button variant="outline" size="sm" disabled={disabled} data-testid="add-stage" onClick={() => addStage()}>
                            <Plus className="w-4 h-4 mr-1" /> Add stage
                        </Button>
                    </div>
                    <div className="p-4 space-y-2">
                        {headerRow}
                        {stages.map(renderRow)}
                        {stages.length === 0 && <p className="text-sm text-stone-400 py-4 text-center">No stages — add one.</p>}
                    </div>
                </div>
            )}

            {journey ? (
                <p className="text-xs text-stone-500" data-testid="journey-default-stage">New guests start in To invite.</p>
            ) : (
            <div>
                <label className="block text-xs font-medium text-stone-600 mb-1" htmlFor="pipeline-default-stage">
                    {kind === 'contact' ? 'Place new form leads in' : kind === 'deal' ? 'Default stage' : 'Default stage (before the event)'}
                </label>
                <select id="pipeline-default-stage" className={inputCls} value={draft.defaultUid ?? ''} disabled={disabled}
                        data-testid="pipeline-default-stage"
                        onChange={(e) => commit({ ...draft, defaultUid: e.target.value === '' ? null : e.target.value })}>
                    {kind === 'contact' && <option value="">None — don't place new leads</option>}
                    {defaultOptions.map((s) => <option key={s.uid} value={s.uid}>{s.label || '(new stage)'}</option>)}
                </select>
                <p className="text-xs text-stone-500 mt-1">
                    {kind === 'contact' && 'Applies to contacts created by forms. Existing contacts are never moved.'}
                    {kind === 'deal' && 'Used when a deal is created without a stage. Closed Won and Closed Lost cannot be the default.'}
                    {kind === 'event_contact' && 'A new contact in this workflow starts here.'}
                </p>
            </div>
            )}
        </div>
    );
}
